import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  BarChart3, 
  Link2, 
  MousePointerClick, 
  Mail, 
  Download,
  Trash2,
  Building2,
  Calendar,
  Loader2
} from "lucide-react";
import { exportSEOPDF, exportAdsPDF, exportEmailPDF } from "../utils/reportUtils";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const AdminViewReportsPage = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [activeTab, setActiveTab] = useState("seo");
  const [seoReports, setSeoReports] = useState([]);
  const [adsReports, setAdsReports] = useState([]);
  const [emailReports, setEmailReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchReports();
    } else {
      setSeoReports([]);
      setAdsReports([]);
      setEmailReports([]);
    }
  }, [selectedClient]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const [seoRes, adsRes, emailRes] = await Promise.all([
        axios.get(`${API_URL}/reports/seo?client_id=${selectedClient}`),
        axios.get(`${API_URL}/reports/ads?client_id=${selectedClient}`),
        axios.get(`${API_URL}/reports/email?client_id=${selectedClient}`)
      ]);
      setSeoReports(seoRes.data);
      setAdsReports(adsRes.data);
      setEmailReports(emailRes.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Erro ao carregar relatórios");
    }
  };

  const handleDeleteSeoReport = async (reportId) => {
    if (!window.confirm("Tem certeza que deseja eliminar este relatório SEO?")) return;
    try {
      await axios.delete(`${API_URL}/reports/seo/${reportId}`);
      toast.success("Relatório SEO eliminado");
      fetchReports();
    } catch (error) {
      toast.error("Erro ao eliminar relatório");
    }
  };

  const handleDeleteAdsReport = async (reportId) => {
    if (!window.confirm("Tem certeza que deseja eliminar este relatório Google Ads?")) return;
    try {
      await axios.delete(`${API_URL}/reports/ads/${reportId}`);
      toast.success("Relatório Google Ads eliminado");
      fetchReports();
    } catch (error) {
      toast.error("Erro ao eliminar relatório");
    }
  };

  const handleDeleteEmailReport = async (reportId) => {
    if (!window.confirm("Tem certeza que deseja eliminar este relatório Email?")) return;
    try {
      await axios.delete(`${API_URL}/reports/email/${reportId}`);
      toast.success("Relatório Email eliminado");
      fetchReports();
    } catch (error) {
      toast.error("Erro ao eliminar relatório");
    }
  };

  const handleExportSEO = async () => {
    setExporting("seo");
    try {
      await exportSEOPDF(selectedClient);
      toast.success("PDF SEO exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar PDF: " + error.message);
    } finally {
      setExporting("");
    }
  };

  const handleExportAds = async () => {
    setExporting("ads");
    try {
      await exportAdsPDF(selectedClient);
      toast.success("PDF Google Ads exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar PDF: " + error.message);
    } finally {
      setExporting("");
    }
  };

  const handleExportEmail = async () => {
    setExporting("email");
    try {
      await exportEmailPDF(selectedClient);
      toast.success("PDF Email exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar PDF: " + error.message);
    } finally {
      setExporting("");
    }
  };

  // Calculate ROAS for Ads
  const calculateROAS = (r) => {
    if (!r || !r.total_spend || r.total_spend === 0) return 0;
    return (r.revenue || 0) / r.total_spend;
  };

  const selectedClientData = clients.find(c => c.id === selectedClient);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-64" />
        <Card><CardContent className="pt-6"><div className="skeleton h-48" /></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-view-reports-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
          <BarChart3 className="w-8 h-8 text-blue-600" />
          Consultar Relatórios
        </h1>
        <p className="text-slate-500 mt-1">
          Visualize todos os relatórios de cada cliente
        </p>
      </div>

      {/* Client Selection */}
      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
            Selecionar Cliente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="max-w-md">
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger data-testid="select-view-client">
                  <SelectValue placeholder="Escolha um cliente para ver os relatórios" />
                </SelectTrigger>
                <SelectContent>
                  {clients.length === 0 ? (
                    <SelectItem value="none" disabled>Sem clientes disponíveis</SelectItem>
                  ) : (
                    clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          {client.company} - {client.name}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            
            {selectedClientData && (
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Cliente selecionado</p>
                  <p className="font-semibold text-slate-900">{selectedClientData.company}</p>
                </div>
                <Badge variant="outline" className={selectedClientData.active ? "badge-success" : "badge-error"}>
                  {selectedClientData.active ? "Ativo" : "Inativo"}
                </Badge>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reports Tabs */}
      {selectedClient ? (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="seo" className="flex items-center gap-2" data-testid="tab-view-seo">
              <Link2 className="w-4 h-4" />
              SEO ({seoReports.length})
            </TabsTrigger>
            <TabsTrigger value="ads" className="flex items-center gap-2" data-testid="tab-view-ads">
              <MousePointerClick className="w-4 h-4" />
              Ads ({adsReports.length})
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2" data-testid="tab-view-email">
              <Mail className="w-4 h-4" />
              Email ({emailReports.length})
            </TabsTrigger>
          </TabsList>

          {/* SEO Reports Tab */}
          <TabsContent value="seo">
            <Card className="stat-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Link2 className="w-5 h-5 text-blue-600" />
                  Relatórios SEO
                </CardTitle>
                {seoReports.length > 0 && (
                  <Button onClick={handleExportSEO} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="export-seo-pdf" disabled={exporting === "seo"}>
                    {exporting === "seo" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    {exporting === "seo" ? "A exportar..." : "Exportar PDF"}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {seoReports.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Período</TableHead>
                          <TableHead className="text-right">Backlinks</TableHead>
                          <TableHead className="text-right">DA Médio</TableHead>
                          <TableHead className="text-right">Novos Domínios</TableHead>
                          <TableHead className="text-right">Menções</TableHead>
                          <TableHead className="text-right">Perdidos</TableHead>
                          <TableHead className="text-right">Disavow</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {seoReports.map((report) => (
                          <TableRow key={report.id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                {report.period_start} a {report.period_end}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{(report.total_backlinks_acquired || 0).toLocaleString('pt-PT')}</TableCell>
                            <TableCell className="text-right">{(report.avg_domain_authority || 0).toFixed(1)}</TableCell>
                            <TableCell className="text-right">{(report.new_referring_domains || 0).toLocaleString('pt-PT')}</TableCell>
                            <TableCell className="text-right">{(report.brand_mentions || 0).toLocaleString('pt-PT')}</TableCell>
                            <TableCell className="text-right text-red-600">{(report.links_removed_lost || 0)}</TableCell>
                            <TableCell className="text-right text-orange-600">{(report.disavow_links_added || 0)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteSeoReport(report.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-seo-${report.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <Link2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Sem relatórios SEO para este cliente</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ads Reports Tab */}
          <TabsContent value="ads">
            <Card className="stat-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <MousePointerClick className="w-5 h-5 text-amber-600" />
                  Relatórios Google Ads
                </CardTitle>
                {adsReports.length > 0 && (
                  <Button onClick={handleExportAds} size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid="export-ads-pdf" disabled={exporting === "ads"}>
                    {exporting === "ads" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    {exporting === "ads" ? "A exportar..." : "Exportar PDF"}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {adsReports.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Período</TableHead>
                          <TableHead className="text-right">Spend (€)</TableHead>
                          <TableHead className="text-right">Impressões</TableHead>
                          <TableHead className="text-right">Cliques</TableHead>
                          <TableHead className="text-right">CTR</TableHead>
                          <TableHead className="text-right">Conversões</TableHead>
                          <TableHead className="text-right">ROAS</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {adsReports.map((report) => (
                          <TableRow key={report.id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                {report.period_start} a {report.period_end}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{(report.total_spend || 0).toFixed(2)}€</TableCell>
                            <TableCell className="text-right">{(report.impressions || 0).toLocaleString('pt-PT')}</TableCell>
                            <TableCell className="text-right">{(report.clicks || 0).toLocaleString('pt-PT')}</TableCell>
                            <TableCell className="text-right">{(report.ctr || 0).toFixed(2)}%</TableCell>
                            <TableCell className="text-right">{(report.conversions || 0)}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant="outline" className={calculateROAS(report) >= 1 ? "badge-success" : "badge-warning"}>
                                {calculateROAS(report).toFixed(2)}x
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteAdsReport(report.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-ads-${report.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <MousePointerClick className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Sem relatórios Google Ads para este cliente</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Reports Tab */}
          <TabsContent value="email">
            <Card className="stat-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Mail className="w-5 h-5 text-emerald-600" />
                  Relatórios Email Marketing
                </CardTitle>
                {emailReports.length > 0 && (
                  <Button onClick={handleExportEmail} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-email-pdf" disabled={exporting === "email"}>
                    {exporting === "email" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    {exporting === "email" ? "A exportar..." : "Exportar PDF"}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {emailReports.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Período</TableHead>
                          <TableHead>Plataforma</TableHead>
                          <TableHead className="text-right">Enviados</TableHead>
                          <TableHead className="text-right">Entregues</TableHead>
                          <TableHead className="text-right">Open Rate</TableHead>
                          <TableHead className="text-right">Click Rate</TableHead>
                          <TableHead className="text-right">Revenue (€)</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {emailReports.map((report) => (
                          <TableRow key={report.id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                {report.period_start} a {report.period_end}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{report.platform || 'Brevo'}</Badge>
                            </TableCell>
                            <TableCell className="text-right">{(report.emails_sent || 0).toLocaleString('pt-PT')}</TableCell>
                            <TableCell className="text-right">{(report.delivered || 0).toLocaleString('pt-PT')}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant="outline" className="badge-success">
                                {(report.open_rate || 0).toFixed(1)}%
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">{(report.click_rate || 0).toFixed(1)}%</TableCell>
                            <TableCell className="text-right text-emerald-600">{(report.revenue || 0).toFixed(2)}€</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteEmailReport(report.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-email-${report.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Sem relatórios Email Marketing para este cliente</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      ) : (
        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="text-center py-12 text-slate-400">
              <Building2 className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Selecione um cliente para ver os seus relatórios</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminViewReportsPage;
